import jwtDecode from 'jwt-decode';

import { ConfluenceSpaceAri } from '@atlassian/ari/confluence/space';
import { CanvasBoardAri } from '@atlassian/ari/canvas/board';
import { CanvasDatabaseAri } from '@atlassian/ari/canvas/database';

import type {
	ContentTypeDataSecurityPolicy,
	ContentTypeTokenFeatures,
	DecodedAccessToken,
} from '@confluence/content-types-bridge/entry-points/content-types-shell-types.ts';
import { hashData } from '@confluence/hash';
import { fg } from '@confluence/feature-gating';

import { WhiteboardTokenQuery } from '../graphql/WhiteboardTokenQuery.graphql.ts';
import type {
	WhiteboardTokenQuery as WhiteboardTokenQueryType,
	WhiteboardTokenQueryVariables,
} from '../graphql/__types__/WhiteboardTokenQuery.ts';
import type {
	ExternalWhiteboardTokenQuery as ExternalWhiteboardTokenQueryType,
	ExternalWhiteboardTokenQueryVariables,
} from '../graphql/__types__/ExternalWhiteboardTokenQuery.ts';
import type { WhiteboardToken } from '../whiteboard-shell-types.ts';
import { ExternalWhiteboardTokenQuery } from '../graphql/ExternalWhiteboardTokenQuery.graphql.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

/**
 * undefined represents no token/the token has be removed
 */
type TokenChangeHandler = (token: WhiteboardToken | undefined) => void;

type TokenContext = {
	spaceId: string;
	tenantId: string;
	contentId: string;
	confluenceEdition: string;
	isExternal: boolean;
	// shareToken is only used for public links
	shareToken?: string;
	dataSecurityPolicyPageExport: boolean | null;
	dataSecurityPolicyAttachmentDownload: boolean | null;
	isAIEnabledForContent: boolean;
	isRovoEnabledForContent: boolean;
};
const emptyContext = (): TokenContext => ({
	spaceId: '',
	tenantId: '',
	contentId: '',
	confluenceEdition: '',
	isExternal: false,
	shareToken: '',
	dataSecurityPolicyPageExport: null,
	dataSecurityPolicyAttachmentDownload: null,
	isAIEnabledForContent: false,
	isRovoEnabledForContent: false,
});

/**
 * Used to refer to the 'home' view.
 */
const FALLBACK_HOME_KEY = 'root';

/**
 * Used to refer to the 'create' view.
 */
const FALLBACK_CREATE_KEY = 'create';

/**
 * Set to a value a little below the server timeout to account for networking delays and retries.
 */
const EXPIRY_TIME_MILLSECONDS = 9 * 60 * 1000;

/**
 * Note: This is currently a module scope value as we recreate the token service on SPA transitions. This would cause us to fetch cached tokens on the first transition to a board which would result in a token expiry errors.
 * @see {@link https://atlassian.slack.com/archives/C027MLUFSFN/p1717038827909519?thread_ts=1716372417.294799&cid=C027MLUFSFN}
 */
let isFirstLoad = true;

const allowedDocumentTypes = ['whiteboard', 'database'] as const;
export type DocumentType = (typeof allowedDocumentTypes)[number];

export class WhiteboardTokenService {
	documentType: DocumentType;
	handlers: TokenChangeHandler[];
	context: TokenContext;
	token: WhiteboardToken | undefined;
	cache: Map<
		string,
		{
			promise: Promise<WhiteboardToken | undefined>;
			resolved: boolean;
			expiresAt?: number;
		}
	>;
	invalidTokenHandler: () => void;
	// @ts-ignore
	tokenErrorHandler: (error) => void;

	constructor(documentType: DocumentType) {
		this.documentType = documentType;
		this.handlers = [];
		this.context = emptyContext();
		this.token = undefined;
		this.cache = new Map();
		this.invalidTokenHandler = () => {};
		this.tokenErrorHandler = () => {};
	}

	dispose() {
		this.handlers = [];
		this.context = emptyContext();
		this.token = undefined;
		this.cache = new Map();
	}

	/**
	 * Sets the context of the token service.
	 * @description Used to fetch tokens pinned to space & site.
	 */
	setContext({
		spaceId,
		tenantId,
		contentId,
		confluenceEdition,
		isExternal,
		shareToken,
		dataSecurityPolicyPageExport,
		dataSecurityPolicyAttachmentDownload,
		isAIEnabledForContent,
		isRovoEnabledForContent,
	}: TokenContext) {
		this.context.spaceId = spaceId;
		this.context.tenantId = tenantId;
		this.context.contentId = contentId;
		this.context.confluenceEdition = confluenceEdition;
		this.context.isExternal = isExternal;
		this.context.shareToken = shareToken;
		this.context.dataSecurityPolicyPageExport = dataSecurityPolicyPageExport;
		this.context.dataSecurityPolicyAttachmentDownload = dataSecurityPolicyAttachmentDownload;
		this.context.isAIEnabledForContent = isAIEnabledForContent;
		this.context.isRovoEnabledForContent = isRovoEnabledForContent;
	}

	/**
	 * A method called on every route transition on whiteboards.
	 * @returns <void> Sets a fresh token with encoded permissions.
	 */
	updateToken = async (referenceId?: string) => {
		const token = await this.preloadToken(referenceId);

		this.token = token;
		this.handlers.forEach((handler) => handler(token));
	};

	/**
	 * A method called on every route transition on whiteboards to empty the existing token.
	 * @returns <void> Sets an empty token with encoded permissions.
	 */
	resetToken = () => {
		this.token = undefined;
		this.context = emptyContext();
		this.handlers.forEach((handler) => handler(undefined));
	};

	/**
	 * Setup listeners for token changes.
	 * @description Called everytime the token updates.
	 */
	onTokenChange = (cb: TokenChangeHandler) => {
		this.handlers.push(cb);
	};

	/**
	 * Setup listener for invalid token
	 * @description Called within preloadToken if the token is lacking sufficient whiteboard permissions.
	 */
	onInvalidToken = (cb: () => void) => {
		this.invalidTokenHandler = cb;
	};

	/**
	 * Setup listener for a token error
	 * @description Called within loadToken's catch in case there is an error loading the token
	 */
	// @ts-ignore
	onTokenLoadError = (cb: (error) => void) => {
		this.tokenErrorHandler = cb;
	};

	/**
	 * Checks if the error message indicates that token data is missing.
	 * @description Used to determine if the experience should be aborted due to missing token data.
	 * @returns boolean indicating whether the error message contains 'missing token data'.
	 */
	hasMissingTokenData = (message: string): boolean => {
		if (message.includes('missing token data')) {
			// Missing accessToken, mediaToken, or mediaBaseUrl necessary for whiteboards to function
			return true;
		}
		return false;
	};

	/**
	 * Setup listeners for token initialisation.
	 * @description Called once at the start of the lifetime of the whiteboard frame.
	 */
	onTokenInit = (cb: TokenChangeHandler) => {
		cb(this.token);
	};

	private getHashKey = (id: string) => {
		return hashData(
			JSON.stringify({
				id,
				...this.context,
			}),
		);
	};

	/**
	 * Starts preloading a token, and stores the result in {@link cache}.
	 * @description Implements a mutex by keeping one promise per content id.
	 */
	preloadToken = (id?: string) => {
		const key = id ? this.getHashKey(id) : FALLBACK_HOME_KEY;
		const entry = this.cache.get(key);

		const now = Date.now();
		const fetching = entry?.promise;
		const resolved = entry?.resolved;
		const expired = Boolean(entry?.expiresAt && now >= entry.expiresAt);

		if (fetching) {
			if (!resolved) {
				return fetching;
			}

			if (!expired) {
				return fetching;
			} else {
				this.cache.delete(key);
			}
		}

		const promise = this.loadToken(id);
		this.cache.set(key, { promise, resolved: false });

		promise
			.then((token) => {
				// Do not hold onto empty tokens
				if (!this.hasToken(token)) {
					this.cache.delete(key);
					return;
				}

				if (!this.isValidToken(token, key)) {
					this.cache.delete(key);
					this.invalidTokenHandler();
					return;
				}

				this.cache.set(key, {
					promise,
					resolved: true,
					expiresAt: now + EXPIRY_TIME_MILLSECONDS,
				});
			})
			.catch((_err) => {
				// Do not hold onto tokens which failed to be fetched
				this.cache.delete(key);
			});

		return promise;
	};

	/**
	 * Gives the consumer the existing token for a route, or starts fetching one.
	 * @description Uses the same mutex implemented in {@link preloadToken}
	 */
	getToken = (id?: string) => {
		return this.preloadToken(id);
	};

	/**
	 * A method for forcing refresh of a token.
	 * @description Used when permissions change on a whiteboard, forcing users
	 * to refetch a new token.
	 * @description Delegates to {@link revokeToken} to revoke the current token.
	 * @returns <void>
	 */
	refreshToken = async (id?: string) => {
		this.revokeToken(id);
		await this.updateToken(id);
	};

	/**
	 * @description Method for taking an encoded access token and determining whether or
	 * not it has read permissions for the document
	 * @returns boolean value indicating whether the provided token has sufficient
	 * document permissions or not
	 */
	isValidToken(token: WhiteboardToken | undefined, key: string): boolean {
		if (token?.accessToken) {
			/**
			 * If on home or create routes, existence of a token is enough to ensure
			 * the user has permission to take the provided action (view home or create).
			 */
			if (key === FALLBACK_HOME_KEY) {
				return true;
			}
			if (key === FALLBACK_CREATE_KEY) {
				return true;
			}

			const decodedAccessToken: DecodedAccessToken = jwtDecode(token.accessToken);
			const documentARI = Object.keys(decodedAccessToken.access).find((ari) => {
				return CanvasBoardAri.check(ari) || CanvasDatabaseAri.check(ari);
			});
			if (documentARI) {
				const documentAccess = decodedAccessToken.access[documentARI];
				if (documentAccess && documentAccess.includes('read')) {
					return true;
				}
			}
		}
		return false;
	}

	/**
	 * @description Method for taking an encoded access token and returning the permissions
	 * @returns an empty array if no claims exist. An array with [read] for read only or [read, write] if editable
	 */
	getPermissions(token: WhiteboardToken | undefined): string[] {
		if (token?.accessToken) {
			const decodedAccessToken: DecodedAccessToken = jwtDecode(token.accessToken);
			const documentARI = Object.keys(decodedAccessToken.access).find((ari) => {
				return CanvasBoardAri.check(ari) || CanvasDatabaseAri.check(ari);
			});
			if (documentARI) {
				return decodedAccessToken.access[documentARI];
			}
		}
		return [];
	}

	/**
	 * A method for revoking a token.
	 * @description Used when permissions change on a whiteboard, forcing users
	 * to refetch a new token.
	 * @returns <void>
	 */
	private revokeToken = (id?: string) => {
		const key = id ? this.getHashKey(id) : FALLBACK_HOME_KEY;

		this.token = undefined;
		this.cache.delete(key);

		this.handlers.forEach((handler) => handler(undefined));
	};

	/**
	 * A method for getting the board ari.
	 * @description Used to determine the board ari for a given reference id and document type.
	 * @param referenceId
	 * @param tenantId
	 * @private
	 */
	private getBoardAri = (referenceId: string, tenantId: string) => {
		return this.documentType == 'whiteboard'
			? CanvasBoardAri.create({
					siteId: tenantId,
					boardId: referenceId,
				})
			: CanvasDatabaseAri.create({
					siteId: tenantId,
					databaseId: referenceId,
				});
	};

	/**
	 * Loads a token by reaching out to a specific token URL.
	 * @param referenceId
	 */
	private async loadToken(referenceId: string | undefined): Promise<WhiteboardToken | undefined> {
		// We cannot fetch a token without a context.
		// Hence, we default to 'empty'.
		if (!this.hasContext()) {
			return undefined;
		}

		const handleError = (err: Error): undefined => {
			this.tokenErrorHandler(err);
			return undefined;
		};

		const {
			spaceId,
			tenantId,
			contentId,
			shareToken,
			isExternal,
			dataSecurityPolicyPageExport,
			dataSecurityPolicyAttachmentDownload,
			isAIEnabledForContent,
			isRovoEnabledForContent,
		} = this.context;

		try {
			const tokenPromise =
				isExternal && shareToken
					? await fetchExternalTokenDataByGraphQL(shareToken)
					: await fetchTokenDataByGraphQL(contentId, isFirstLoad);
			isFirstLoad = false;

			const { accessToken, mediaToken, mediaBaseUrl, whiteboardEntitlements } = tokenPromise;

			if (!accessToken || !mediaToken || !mediaBaseUrl) {
				const missingItems = [
					...(!accessToken ? ['accessToken'] : []),
					...(!mediaToken ? ['mediaToken'] : []),
					...(!mediaBaseUrl ? ['mediaBaseUrl'] : []),
				];
				const missingTokenError = new Error(`missing token data for: ${missingItems.join(', ')}`);
				return handleError(missingTokenError);
			}

			const containerAri = ConfluenceSpaceAri.create({
				siteId: tenantId,
				spaceId,
			});
			const boardAri = referenceId ? this.getBoardAri(referenceId, tenantId) : '';
			const features: ContentTypeTokenFeatures = {
				whiteboards: {
					...whiteboardEntitlements.whiteboards,
					aiGenerate: {
						isEntitled: isAIEnabledForContent,
					},
					aiClustering: {
						isEntitled: isAIEnabledForContent,
					},
					aiTeammate: {
						isEntitled: isAIEnabledForContent && isRovoEnabledForContent,
					},
					aiSmartButton: {
						isEntitled: isAIEnabledForContent && isRovoEnabledForContent,
					},
					aiJiraSearch: {
						isEntitled: isAIEnabledForContent,
					},
					jiraIssueUnfurl: {
						isEntitled: whiteboardEntitlements.whiteboards?.smartConnectors?.isEntitled ?? false,
					},
				},
			};
			let policies:
				| {
						policies: {
							dataSecurityPolicy: ContentTypeDataSecurityPolicy;
						};
				  }
				| {} = {};
			if (fg('cc-dlp_sensible-defaults-for-canvas-bridge-dsps')) {
				const dataSecurityPolicy: ContentTypeDataSecurityPolicy = {
					pageExport: dataSecurityPolicyPageExport === true,
					attachmentDownload: fg('cc-dlp_split-attachment-dl-from-export-dsp')
						? dataSecurityPolicyAttachmentDownload === true
						: dataSecurityPolicyPageExport === true,
				};
				policies = { policies: { dataSecurityPolicy } };
			} else {
				const dataSecurityPolicy: ContentTypeDataSecurityPolicy = {};
				if (fg('cc-dlp_split-attachment-dl-from-export-dsp')) {
					if (dataSecurityPolicyPageExport != null) {
						dataSecurityPolicy.pageExport = dataSecurityPolicyPageExport;
					}
					if (dataSecurityPolicyAttachmentDownload != null) {
						dataSecurityPolicy.attachmentDownload = dataSecurityPolicyAttachmentDownload;
					}
				} else {
					if (dataSecurityPolicyPageExport != null) {
						dataSecurityPolicy.pageExport = dataSecurityPolicyPageExport;
						dataSecurityPolicy.attachmentDownload = dataSecurityPolicyPageExport;
					}
				}
				policies =
					Object.keys(dataSecurityPolicy).length > 0 ? { policies: { dataSecurityPolicy } } : {};
			}

			return {
				container: containerAri.toString(),
				board: boardAri.toString(),
				accessToken,
				mediaToken,
				mediaBaseUrl,
				mediaCollectionId: contentId,
				features,
				edition: this.context.confluenceEdition,
				...policies,
			};
		} catch (err) {
			return handleError(err);
		}
	}

	/**
	 * Checks if there is enough data in {@link context} to fetch a token.
	 */
	private hasContext(): boolean {
		const hasTenantId = this.context.tenantId.length > 0;
		const hasSpaceId = this.context.spaceId.length > 0;
		const hasContentId = this.context.contentId.length > 0;
		const hasConfluenceEdition = Boolean(this.context.confluenceEdition);
		return hasTenantId && hasSpaceId && hasContentId && hasConfluenceEdition;
	}

	/**
	 * Checks if the {@link token} has expected data in it.
	 */
	private hasToken(token: WhiteboardToken | undefined): boolean {
		return Boolean(token && token.accessToken && token.container);
	}
}

type FetchTokenDataResponse = {
	accessToken: string | undefined;
	mediaToken: string | undefined;
	mediaBaseUrl: string | undefined;
	whiteboardEntitlements: {
		whiteboards:
			| NonNullable<
					NonNullable<NonNullable<WhiteboardTokenQueryType['entitlements']>['whiteboardFeatures']>
			  >
			| null
			| undefined;
	};
};

const fetchTokenDataByGraphQL = async (
	contentId: string,
	useCache = false,
): Promise<FetchTokenDataResponse> => {
	const tokenQuery = await queryInterop<WhiteboardTokenQueryType, WhiteboardTokenQueryVariables>({
		query: WhiteboardTokenQuery,
		variables: { contentId },
		context: {
			fetchOptions: {
				ignoreNoNetworkError: true,
			},
		},
		fetchPolicy: useCache ? 'cache-first' : 'no-cache',
	});
	const accessToken = tokenQuery?.data?.canvasToken?.token;
	const readOnlyMediaToken =
		tokenQuery?.data?.contentMediaSession?.accessTokens?.readOnlyToken?.value;
	const readWriteMediaToken =
		tokenQuery?.data?.contentMediaSession?.accessTokens?.readWriteToken?.value;
	const mediaBaseUrl = tokenQuery?.data?.contentMediaSession?.configuration?.fileStoreUrl;
	const whiteboardEntitlements = tokenQuery?.data?.entitlements?.whiteboardFeatures;

	return {
		accessToken,
		mediaToken: readWriteMediaToken || readOnlyMediaToken,
		mediaBaseUrl,
		whiteboardEntitlements: { whiteboards: whiteboardEntitlements },
	};
};

const fetchExternalTokenDataByGraphQL = async (
	shareToken: string,
): Promise<FetchTokenDataResponse> => {
	const tokenQuery = await queryInterop<
		ExternalWhiteboardTokenQueryType,
		ExternalWhiteboardTokenQueryVariables
	>({
		query: ExternalWhiteboardTokenQuery,
		variables: { shareToken },
		context: {
			allowOnExternalPage: true,
			fetchOptions: {
				ignoreNoNetworkError: true,
			},
		},
		fetchPolicy: 'no-cache',
	});

	const data = tokenQuery?.data;
	const accessToken = data?.externalCanvasToken?.token;
	const readOnlyMediaToken = data?.externalContentMediaSession?.accessTokens?.readOnlyToken?.value;
	const readWriteMediaToken =
		data?.externalContentMediaSession?.accessTokens?.readWriteToken?.value;
	const mediaBaseUrl = tokenQuery?.data?.externalContentMediaSession?.configuration?.fileStoreUrl;
	const whiteboardEntitlements = data?.entitlements?.whiteboardFeatures;

	return {
		accessToken,
		mediaToken: readWriteMediaToken || readOnlyMediaToken,
		mediaBaseUrl,
		whiteboardEntitlements: { whiteboards: whiteboardEntitlements },
	};
};
